import { useEffect, useRef, useState } from 'react'
import { Alert } from '@mui/material'
import dayjs from 'dayjs'
import axiosInstance from '../../../utils/axiosInstance'

export interface AtsPushState {
  atsPushStatus?: string | null
  atsPushDueAt?: string | null
  atsPushMessage?: string | null
  atsPushAttempts?: number | null
}

/** States that change by themselves within seconds to minutes, so worth watching. */
const isInFlight = (status?: string | null) => status === 'confirming' || status === 'pushing'

const POLL_INTERVAL_MS = 10 * 1000
// Stop watching after 5 minutes; an ATS that slow is picked up on the next open.
const MAX_POLLS = 30

/**
 * A lead's ATS push state that keeps itself current while the push is in flight.
 *
 * The server confirms delivery in the background, so a lead view opened right after a
 * push would otherwise show "waiting for the ATS" until it is reloaded. While `active`
 * (the view is open) and the state is in flight, this re-reads the one lead every 10
 * seconds — not the surrounding list, whose reload would replace the view with a spinner.
 *
 * `loaded` is the state that came with the lead. `onSettled` is called once with the
 * fresh lead when the push leaves the in-flight states (e.g. to pick up a lead status
 * the server reset after a failed push). `confirmedNow` is true when this view saw the
 * ATS confirm the application.
 */
export const useAtsPushState = (
  leadId: string | null | undefined,
  loaded: AtsPushState | undefined,
  active: boolean,
  onSettled?: (lead: { status?: string } & AtsPushState) => void
) => {
  const [state, setState] = useState<AtsPushState | undefined>(loaded)
  const [confirmedNow, setConfirmedNow] = useState(false)
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled

  // Follow the loaded data when the lead, or its loaded state, changes.
  useEffect(() => {
    setState(loaded)
    setConfirmedNow(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, loaded?.atsPushStatus, loaded?.atsPushDueAt])

  const watching = active && !!leadId && isInFlight(state?.atsPushStatus)

  useEffect(() => {
    if (!watching) return undefined

    let polls = 0
    let stopped = false
    const timer = setInterval(async () => {
      polls += 1
      if (polls > MAX_POLLS) {
        clearInterval(timer)
        return
      }
      try {
        const { data } = await axiosInstance.get(`/lead/${leadId}`)
        if (stopped || isInFlight(data?.atsPushStatus)) return
        setConfirmedNow(data?.atsPushStatus === 'pushed')
        setState({
          atsPushStatus: data?.atsPushStatus,
          atsPushDueAt: data?.atsPushDueAt,
          atsPushMessage: data?.atsPushMessage,
          atsPushAttempts: data?.atsPushAttempts,
        })
        onSettledRef.current?.(data)
      } catch {
        // A failed poll is not worth surfacing; the next one, or a reload, catches up.
      }
    }, POLL_INTERVAL_MS)

    return () => {
      stopped = true
      clearInterval(timer)
    }
  }, [watching, leadId])

  return { atsPush: state, setAtsPush: setState, confirmedNow }
}

const formatTime = (date?: string | null) => (date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '')

/**
 * Where the lead's ATS push stands, shown next to the manual push button.
 * Renders nothing for leads that auto-push never handled, or whose delivery was
 * confirmed earlier (`confirmedNow` shows the confirmation the moment it arrives).
 * The states are set by the server's atsIntegrations/autoPush.ts.
 */
const AtsPushStatusNote = ({
  atsPushStatus,
  atsPushDueAt,
  atsPushMessage,
  atsPushAttempts,
  confirmedNow,
}: AtsPushState & { confirmedNow?: boolean }) => {
  const sx = { mt: 1, maxWidth: 420 }

  switch (atsPushStatus) {
    case 'pending':
      return atsPushAttempts ? (
        <Alert severity="warning" sx={sx}>
          ATS push failed, retrying automatically at {formatTime(atsPushDueAt)}.
          {atsPushMessage ? ` Error: ${atsPushMessage}` : ''}
        </Alert>
      ) : (
        <Alert severity="info" sx={sx}>
          Waiting for the applicant&apos;s CV. Sent to the ATS automatically by{' '}
          {formatTime(atsPushDueAt)}.
        </Alert>
      )
    case 'pushing':
      return (
        <Alert severity="info" sx={sx}>
          Sending to the ATS…
        </Alert>
      )
    case 'confirming':
      return (
        <Alert severity="info" sx={sx}>
          Sent. Waiting for the ATS to confirm it received the application.
        </Alert>
      )
    case 'pushed':
      return confirmedNow ? (
        <Alert severity="success" sx={sx}>
          The ATS confirmed it received the application.
        </Alert>
      ) : null
    case 'failed':
      return (
        <Alert severity="error" sx={sx}>
          ATS push failed{atsPushMessage ? `: ${atsPushMessage}` : ''}. Please push it manually.
        </Alert>
      )
    case 'skipped':
      return (
        <Alert severity="info" sx={sx}>
          Not sent to the ATS automatically{atsPushMessage ? `: ${atsPushMessage}` : ''}.
        </Alert>
      )
    default:
      return null
  }
}

export default AtsPushStatusNote
